import { useEffect, useMemo, useState } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Input, Select, Space, Tag, Tooltip, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Permission, type CatalogCategoryResponse, type CatalogoResponse } from '@aid/shared';
import { AdminListToolbar } from '../../../components/AdminListToolbar';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { paginationProps } from '../../../lib/paginacion';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';
import { useCatalogos } from '../api/useCatalogos';
import { useCatalogosAdminList, useDeleteCatalogoAdmin } from '../api/useCatalogosAdmin';
import { DeleteCatalogoConfirmModal } from './DeleteCatalogoConfirmModal';

interface CatalogosTableProps {
  category: CatalogCategoryResponse;
}

const ACTIVE_FILTER = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

type ActiveFilter = (typeof ACTIVE_FILTER)[keyof typeof ACTIVE_FILTER];

export function CatalogosTable({ category }: CatalogosTableProps) {
  const t = useT();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [parentId, setParentId] = useState<number | undefined>();
  const [activeFilter, setActiveFilter] = useState<ActiveFilter | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<CatalogoResponse | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim());
  const { page, pageSize, sort, handleChange, setPage } = useServerPaginacion<CatalogoResponse>(20);
  const deleteCatalogo = useDeleteCatalogoAdmin();

  const canCreate = useHasPermission(Permission.CATALOGOS_CREATE);
  const canUpdate = useHasPermission(Permission.CATALOGOS_UPDATE);
  const canDelete = useHasPermission(Permission.CATALOGOS_DELETE);
  const canHardDelete = canDelete && category.behavior.lifecycle.allowHardDelete;
  const hasHierarchy = category.behavior.hierarchy.enabled;
  const parentCategoryCode = category.parentCategoryCode ?? undefined;
  const { data: parentItems, isLoading: parentItemsLoading } = useCatalogos(
    parentCategoryCode ?? '',
    { limit: 1000, enabled: Boolean(parentCategoryCode) },
  );

  useEffect(() => {
    setPage(1);
  }, [category.code, debouncedSearch, parentId, activeFilter, setPage]);

  const { data, isLoading, isFetching, error, refetch } = useCatalogosAdminList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: debouncedSearch || undefined,
    category: category.code,
    parentId,
    active: activeFilter === undefined ? undefined : activeFilter === ACTIVE_FILTER.ACTIVE,
    ...sort,
  });

  const parentLabels = useMemo(
    () =>
      new Map(
        (parentItems ?? []).map((item) => [
          Number(item.id),
          item.value ?? item.key ?? `#${item.id}`,
        ]),
      ),
    [parentItems],
  );

  const clearFilters = () => {
    setSearch('');
    setParentId(undefined);
    setActiveFilter(undefined);
    setPage(1);
  };

  const hasFilters = Boolean(
    debouncedSearch || parentId !== undefined || activeFilter !== undefined,
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCatalogo.mutateAsync(Number(deleteTarget.id));
      message.success('Registro eliminado correctamente');
      setDeleteTarget(null);
    } catch {
      message.error('No se pudo eliminar el registro. Puede tener referencias o hijos.');
    }
  };

  if (error) {
    return (
      <EmptyState
        title="No se pudieron cargar los registros"
        description="Reintentá la operación o revisá la conexión con el servidor."
        action={<Button onClick={() => void refetch()}>Reintentar</Button>}
      />
    );
  }

  const columns = [
    {
      title: 'Clave',
      dataIndex: 'key' as keyof CatalogoResponse,
      key: 'key',
      ...serverSortProps('key', sort),
      render: (value: string | null) => value ?? '—',
    },
    {
      title: 'Valor',
      dataIndex: 'value' as keyof CatalogoResponse,
      key: 'value',
      ...serverSortProps('value', sort),
      render: (value: string | null) => value ?? '—',
    },
    ...(hasHierarchy
      ? [
          {
            title: 'Padre',
            dataIndex: 'parent' as keyof CatalogoResponse,
            key: 'parent',
            render: (value: number | null) =>
              value === null || value === undefined
                ? '—'
                : (parentLabels.get(Number(value)) ?? `#${value}`),
          },
        ]
      : []),
    {
      title: 'Estado',
      dataIndex: 'active' as keyof CatalogoResponse,
      key: 'active',
      ...serverSortProps('active', sort),
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>{active ? 'Activo' : 'Inactivo'}</Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 128,
      render: (_value: unknown, record: CatalogoResponse) => (
        <Space size={2}>
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              aria-label="Ver detalle"
              onClick={() => navigate(`/catalogos/${record.id}`)}
            />
          </Tooltip>
          {canUpdate && (
            <Tooltip title="Editar">
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label="Editar"
                onClick={() => navigate(`/catalogos/${record.id}/edit`)}
              />
            </Tooltip>
          )}
          {canHardDelete && (
            <Tooltip title="Eliminar">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label="Eliminar"
                onClick={() => setDeleteTarget(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <AdminListToolbar
        filters={
          <>
            <Input
              aria-label="Buscar registros"
              placeholder="Buscar por clave o valor"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              allowClear
              style={{ width: 280, maxWidth: '100%' }}
            />
            {hasHierarchy && parentCategoryCode && (
              <Select<number>
                aria-label={`Filtrar por ${parentCategoryCode}`}
                placeholder={`Filtrar por ${parentCategoryCode}`}
                allowClear
                showSearch
                optionFilterProp="label"
                loading={parentItemsLoading}
                value={parentId}
                onChange={(value) => setParentId(value)}
                options={(parentItems ?? []).map((item) => ({
                  value: Number(item.id),
                  label: item.value ?? item.key ?? `#${item.id}`,
                }))}
                style={{ width: 240, maxWidth: '100%' }}
              />
            )}
            <Select<ActiveFilter>
              aria-label="Filtrar por estado"
              placeholder="Filtrar por estado"
              allowClear
              value={activeFilter}
              onChange={(value) => setActiveFilter(value)}
              options={[
                { label: 'Activos', value: ACTIVE_FILTER.ACTIVE },
                { label: 'Inactivos', value: ACTIVE_FILTER.INACTIVE },
              ]}
              style={{ width: 150, maxWidth: '100%' }}
            />
            {hasFilters && <Button onClick={clearFilters}>Limpiar filtros</Button>}
          </>
        }
        actions={
          canCreate ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() =>
                navigate(`/catalogos/new?category=${encodeURIComponent(category.code)}`)
              }
            >
              Nuevo registro
            </Button>
          ) : undefined
        }
      />
      <DataTable<CatalogoResponse>
        columns={columns}
        dataSource={data?.data ?? []}
        rowKey="id"
        loading={isLoading || isFetching}
        pagination={paginationProps(t, {
          page,
          pageSize,
          total: data?.total ?? 0,
        })}
        onChange={handleChange}
        locale={{
          emptyText: (
            <EmptyState
              compact
              title={hasFilters ? 'Sin coincidencias' : 'Sin registros'}
              description={
                hasFilters
                  ? 'No hay registros que coincidan con los filtros actuales.'
                  : 'Esta categoría todavía no tiene registros.'
              }
              action={
                hasFilters ? <Button onClick={clearFilters}>Limpiar filtros</Button> : undefined
              }
            />
          ),
        }}
      />
      {deleteTarget && (
        <DeleteCatalogoConfirmModal
          open
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteCatalogo.isPending}
        />
      )}
    </>
  );
}
